import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, type ReactNode, useState } from "react";
import { ALPHA, FONT_SIZE, ICON_SIZE, NAMED_GROUP, NEUTRAL, SURFACE } from "../../../theme/tokens";
import type { MetaChipTone } from "../Badges/MetaChip";
import { NameDisclosureList, type NameDisclosureSection } from "./NameDisclosureList";

export type NamedGroupTone = "neutral" | "primary" | "success" | "warning" | "info" | "error";

/** One named thing in a group — a person, usually. `id` is required: two employees can share a name. */
export type NamedGroupItem = {
	id: string | number;
	/** Already-translated display label. */
	label: string;
	/** Optional per-name detail ("4h OT · $120") as a toned chip after the name. Already translated. */
	tag?: string;
	/** Tone of `tag`. Default neutral. */
	tagTone?: MetaChipTone;
};

/**
 * A run of names inside the group. Pass one unlabeled section for a flat list; label them when the
 * group splits by reason (e.g. "Skipped" → "doesn't work at this facility" / "already has the job").
 */
export type NamedGroupSection = NameDisclosureSection & { items: NamedGroupItem[] };

type Props = {
	/** Already-translated group name, e.g. "Will change". */
	title: string;
	/** The headline figure — usually the number of items, passed explicitly so a caller can't drift from it. */
	count: number;
	tone?: NamedGroupTone;
	/** Optional leading glyph beside the title, inked in the tone. */
	icon?: ReactNode;
	/** Already-translated line under the title saying what membership in the group means. */
	description?: ReactNode;
	/** A control that acts on the whole group (e.g. a choice for what happens to these people). */
	children?: ReactNode;
	/** Sections with no items are dropped, so a caller can pass every reason. */
	sections: NamedGroupSection[];
	/** Already-translated disclosure labels. `showLabel` receives the item count. */
	showLabel: (count: number) => string;
	hideLabel: string;
	/** Item count at or below which the names start revealed. Default `NAMED_GROUP.AUTO_EXPAND_MAX`. */
	autoExpandMax?: number;
	/**
	 * `columns` (default) packs many short names into a grid; `list` gives each name a full row — use it
	 * when names carry a `tag`, which a narrow grid column would squeeze the name out of.
	 */
	layout?: "list" | "columns";
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

// Neutral takes a mid-grey rather than the text ink: a rail in body-text black out-shouts the toned groups beside it.
const toneAccent = (theme: Theme, tone: NamedGroupTone): string => (tone === "neutral" ? NEUTRAL[450] : theme.palette[tone].main);
const toneInk = (theme: Theme, tone: NamedGroupTone): string => (tone === "neutral" ? theme.palette.text.secondary : theme.palette[tone].dark);

/**
 * A titled, toned group of named things — "Will change (18)", "Have future shifts (4)", "Skipped (3)" —
 * with the names behind a `NameDisclosureList`. The counterpart to `ConflictBlockedDialog`'s tiles for a
 * flow that CAN go ahead: each group is a card, so a review step can stack several outcomes, and a group
 * can carry its own control (what to do with these people) right beside the people it affects.
 *
 * - The tone paints a left rail and a faint header wash, resolved from `theme.palette`, so the groups
 *   are told apart at a glance without any of them shouting — and never by colour alone (the title says it).
 * - Short lists start revealed; long ones are opt-in and scroll inside the card as a multi-column grid.
 * - Copy-free: the caller passes every already-translated string.
 */
export const NamedGroupCard: FC<Props> = ({
	title,
	count,
	tone = "neutral",
	icon,
	description,
	children,
	sections,
	showLabel,
	hideLabel,
	autoExpandMax = NAMED_GROUP.AUTO_EXPAND_MAX,
	// DEFAULT: the grid every group used before `layout` existed, so existing callers don't move.
	layout = "columns",
	dataTestId,
	sx,
}) => {
	const itemCount = sections.reduce((total, section) => total + section.items.length, 0);
	const [open, setOpen] = useState(itemCount <= autoExpandMax);
	const testId = (suffix: string) => (dataTestId ? `${dataTestId}-${suffix}` : undefined);

	return (
		<Box
			component="section"
			aria-label={title}
			data-testid={dataTestId}
			data-tone={tone}
			sx={[
				(theme) => ({
					position: "relative",
					overflow: "hidden",
					borderRadius: 1,
					border: `1px solid ${NEUTRAL[200]}`,
					backgroundColor: SURFACE,
					"&::before": {
						content: '""',
						position: "absolute",
						insetBlock: 0,
						insetInlineStart: 0,
						width: NAMED_GROUP.ACCENT_RAIL_WIDTH,
						backgroundColor: toneAccent(theme, tone),
					},
				}),
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			<Box
				sx={(theme) => ({
					px: 2,
					py: 1.5,
					display: "flex",
					flexDirection: "column",
					gap: 1,
					backgroundColor: alpha(toneAccent(theme, tone), ALPHA.HOVER_OVERLAY),
				})}
			>
				<GroupHeader title={title} count={count} tone={tone} icon={icon} countTestId={testId("count")} />
				{description && (
					<Typography variant="body2" sx={{ color: "text.secondary", fontSize: FONT_SIZE.SECONDARY }}>
						{description}
					</Typography>
				)}
				{children}
			</Box>
			{itemCount > 0 && (
				<NameDisclosureList
					open={open}
					onToggle={() => setOpen((prev) => !prev)}
					showLabel={showLabel(itemCount)}
					hideLabel={hideLabel}
					sections={sections}
					layout={layout}
					maxHeight={NAMED_GROUP.LIST_MAX_HEIGHT}
					dense
					toggleTestId={testId("toggle")}
					listTestId={testId("list")}
					sectionTestId={dataTestId ? (id) => `${dataTestId}-section-${id}` : undefined}
					itemTestId={dataTestId ? (item) => `${dataTestId}-item-${item.id}` : undefined}
					sx={{ px: 1.5, pb: 1, pt: 0.5, borderTop: `1px solid ${NEUTRAL[150]}` }}
				/>
			)}
		</Box>
	);
};

type GroupHeaderProps = { title: string; count: number; tone: NamedGroupTone; icon?: ReactNode; countTestId?: string };

const GroupHeader: FC<GroupHeaderProps> = ({ title, count, tone, icon, countTestId }) => (
	<Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
		{icon && (
			<Box aria-hidden sx={(theme) => ({ display: "flex", color: toneInk(theme, tone), "& svg": { fontSize: ICON_SIZE.INLINE } })}>
				{icon}
			</Box>
		)}
		<Typography component="h3" variant="subtitle2" sx={{ flex: 1, minWidth: 0, fontSize: FONT_SIZE.EMPHASIS, color: "text.primary" }}>
			{title}
		</Typography>
		<Typography
			component="span"
			data-testid={countTestId}
			sx={(theme) => ({
				flexShrink: 0,
				fontSize: FONT_SIZE.TITLE_MD,
				fontWeight: 700,
				lineHeight: 1,
				fontVariantNumeric: "tabular-nums",
				color: toneInk(theme, tone),
			})}
		>
			{count.toLocaleString()}
		</Typography>
	</Box>
);

export default NamedGroupCard;
